import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import NotificationPopup from "../projects/NotificationPopup";
import AddNote from "./AddNote";
import CommentsProject from "./CommentsProject";
import UpdateProjectStage from "./UpdateProjectStage";
import UpdateInstructor from "./UpdateInstructor";
import AddStudentToProject from "./AddStudentToProject";
import Modal from "../base/Modal";
import "../../css/projects/ProjectDetails.css";
import axios from "axios";
import Grades from "./grades";
import Swal from "sweetalert2";
import vars from "../../vars";

const ProjectDetails = () => {
    const { projectId } = useParams();
    const navigate = useNavigate();

    const [project, setProject] = useState(null);
    const [pdfUrl, setPdfUrl] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [notification, setNotification] = useState(null);
    const [showAddNote, setShowAddNote] = useState(false);

    const [commentsSummary, setCommentsSummary] = useState(null);
    const [commentsLoading, setCommentsLoading] = useState(true);
    const [commentsError, setCommentsError] = useState(null);
    const [showComments, setShowComments] = useState(false);
    const [showUpdateStage, setShowUpdateStage] = useState(false);
    const [showUpdateInstructor, setShowUpdateInstructor] = useState(false);
    const [showAddStudents, setShowAddStudents] = useState(false);

    const [department, setDepartment] = useState(null);
    const [isProjectInstructor, setIsProjectInstructor] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const [gradesRefresh, setGradesRefresh] = useState(false);

    const refreshProject = async () => {
        await fetchProjectDetails();
        await fetchIsProjectInstructor();
    };

    const fetchProjectDetails = async () => {
        try {
            const resProject = await fetch(`${vars.apiUrl}/projects/${projectId}`, {
                method: "GET",
                credentials: 'include',
            });
            const dataProject = await resProject.json();
            if (!resProject.ok || !dataProject.success) {
                throw new Error(dataProject.message || "שגיאה בטעינת פרטי הפרויקט");
            }

            const resTech = await fetch(`${vars.apiUrl}/projects/${projectId}/technologies`, {
                method: "GET",
                credentials: 'include',
            });
            const techData = await resTech.json();
            if (!resTech.ok || !techData.success) {
                throw new Error(techData.message || "שגיאה בטעינת טכנולוגיות");
            }

            let newPdfUrl = null;
            const resPdf = await fetch(`${vars.apiUrl}/projects/${projectId}/file`, {
                method: "GET",
                credentials: 'include',
            });

            if (resPdf.ok) {
                const pdfBlob = await resPdf.blob();
                newPdfUrl = URL.createObjectURL(pdfBlob);
            } else if (resPdf.status !== 404) {
                throw new Error("שגיאה בטעינת קובץ ה-PDF");
            }

            setProject({
                ...dataProject.data,
                technologies: techData.data,
                notes: dataProject.data.notes || "אין הערות",
            });
            setPdfUrl(newPdfUrl);
            setLoading(false);
        } catch (err) {
            console.error("שגיאה:", err);
            setError(err.message || "אירעה שגיאה בטעינת פרטי הפרויקט או ה-PDF");
            setLoading(false);
        }
    };

    const fetchCommentsSummary = async () => {
        try {
            setCommentsLoading(true);
            const res = await fetch(`${vars.apiUrl}/comments/project/${projectId}`, {
                method: "GET",
                credentials: "include",
            });
            const data = await res.json();
            if (!res.ok || !data.success) {
                throw new Error(data.message || "שגיאה בטעינת הערות");
            }
            setCommentsSummary(data.data);
        } catch (err) {
            console.error("שגיאה בטעינת הערות:", err);
            setCommentsError(err.message);
        } finally {
            setCommentsLoading(false);
        }
    };

    const departmentName = async function fetchDepartment() {
        try {
            const res = await axios.get(`${vars.apiUrl}/departments`, { withCredentials: true });
            if (res.data && res.data.data) {
                setDepartment(res.data.data);
            }
        } catch (err) {
            console.error("שגיאה בטעינת המגמה:", err);
        }
    }

    const fetchIsProjectInstructor = async () => {
        try {
            const res = await fetch(
                `${vars.apiUrl}/apiInstructor/checkInstructor/${projectId}`,
                {
                    method: "GET",
                    credentials: "include",
                }
            );

            const data = await res.json();

            if (!res.ok || !data.success) {
                throw new Error(data.message || "שגיאה בבדיקת המרצה של הפרויקט");
            }

            setIsProjectInstructor(data.isProjectInstructor);
            setIsAdmin(data.isAdminOfDepartment);


        } catch (err) {
            console.error("שגיאה בבדיקת המרצה של הפרויקט:", err);
        }
    };


    useEffect(() => {
        departmentName();
        fetchProjectDetails();
        fetchCommentsSummary();
        fetchIsProjectInstructor();
    }, [projectId]);

    useEffect(() => {
        return () => {
            if (pdfUrl) {
                URL.revokeObjectURL(pdfUrl);
            }
        };
    }, [pdfUrl]);

    const handleSaveNote = async (noteData) => {
        try {
            const response = await fetch(`${vars.apiUrl}/comments`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({
                    project_id: projectId,
                    title: noteData.title,
                    section: noteData.section,
                    page: noteData.page,
                    text: noteData.text,
                }),
            });

            const data = await response.json();

            if (response.ok && data.success) {
                setNotification({ message: "ההערה נשמרה בהצלחה!", type: "success" });

                await fetchCommentsSummary();
                await fetchProjectDetails();

            } else {
                setNotification({ message: data.message || "שגיאה בשמירת ההערה", type: "error" });
            }
        } catch (error) {
            console.error("שגיאה בשמירת הערה:", error);
            setNotification({ message: "שגיאה בתקשורת עם השרת", type: "error" });
        } finally {
            setShowAddNote(false);
        }
    };

    const handleApproveDocument = async (projectId) => {
        try {
            const result = await Swal.fire({
                title: "אישור מסמך וחתימה",
                text: "בלחיצה על אישור זה יופיע לסטודנט אפשרות להעלות מסמך הפרויקט בפורמט וורד ודוגמת חתימה. האם לאשר?",
                icon: "question",
                showCancelButton: true,
                confirmButtonText: "אישור",
                cancelButtonText: "ביטול",
            });

            if (result.isConfirmed) {
                const res = await fetch(`${vars.apiUrl}/stages/approveDocument/${projectId}`, {
                    method: "POST",
                    credentials: "include",
                });
                const data = await res.json();

                if (res.ok && data.success) {
                    Swal.fire("בוצע!", "סטודנט יוכל להעלות את המסמך כעת.", "success");
                } else {
                    Swal.fire("שגיאה", data.message || "שגיאה באישור המסמך", "error");
                }
            }
        } catch (err) {
            console.error(err);
            Swal.fire("שגיאה", "שגיאת תקשורת עם השרת", "error");
        }
    };


    const handleDownloadApproved = async (projectId) => {
        try {
            const response = await axios.get(
                `${vars.apiUrl}/projects/${projectId}/downloadFiles`,
                { responseType: 'blob', withCredentials: true }
            );
            console.log(response);


            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `project_${projectId}_files.zip`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (err) {
            console.error(err);
            alert("שגיאה בהורדת הקבצים");
        }
    };



    if (loading) {
        return (
            <div className="project-details-wrapper">
                <h2 className="formLabel">פרטי הפרויקט</h2>
                <div className="content-wrapper">
                    <div className="loading">טוען פרטי פרויקט...</div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="project-details-wrapper">
                <h2 className="formLabel">פרטי הפרויקט</h2>
                <div className="content-wrapper">
                    <div className="error">{error}</div>
                </div>
            </div>
        );
    }

    if (!project) {
        return (
            <div className="project-details-wrapper">
                <h2 className="formLabel">פרטי הפרויקט</h2>
                <div className="content-wrapper">
                    <div className="no-project">לא נמצא פרויקט</div>
                </div>
            </div>
        );
    }

    return (

        <div className="project-details-wrapper">
            {notification && (
                <NotificationPopup
                    message={notification.message}
                    type={notification.type}
                    onClose={() => setNotification(null)}
                />
            )}

            <div className="project-header">
                <h2 className="project-title-label-de">פרטי הפרויקט</h2>
                {department && (
                    <h2 className="department-name-label-de">{department.name}</h2>
                )}
            </div>

            <div className="project-main">
                <div className="project-grades-top">
                    <Grades key={gradesRefresh} projectId={projectId}
                    />
                </div>

                <div className="project-grades-button">
                    <div className="content-wrapper">
                        <div className="right-column">
                            <div className="project-details-container">
                                <div className="button-container">
                                    <button className="back-button" onClick={() => navigate(-1)}>
                                        חזרה לכל הפרוייקטים
                                    </button>
                                    {isProjectInstructor && (
                                        <button className="back-button" onClick={() => setShowUpdateStage(true)}>
                                            עדכן שלב פרויקט
                                        </button>
                                    )}

                                    {isAdmin && (
                                        <button className="back-button" onClick={() => setShowUpdateInstructor(true)}>
                                            שנה מנחה
                                        </button>
                                    )}

                                    {isProjectInstructor && (
                                        project.status === 1 ? (
                                            <button
                                                className="back-button"
                                                onClick={() => handleDownloadApproved(projectId)}
                                            >
                                                הורדת מסמך וחתימה
                                            </button>
                                        ) : (
                                            <button
                                                className="back-button"
                                                onClick={() => handleApproveDocument(projectId)}
                                            >
                                                אישור מסמך וחתימה
                                            </button>
                                        )
                                    )}
                                </div>

                                <h2 className="project-title">{project.title}</h2>
                                <div className="project-title">
                                    סטודנט: {project.student1_first_name} {project.student1_last_name}
                                    {project.student2_first_name && project.student2_last_name ? (
                                        <> | {project.student2_first_name} {project.student2_last_name}</>
                                    ) : (
                                        isAdmin&& (
                                            <button onClick={() => setShowAddStudents(true)} className="add-student-btn">
                                                להוספת סטודנט לפרויקט
                                            </button>
                                        )
                                    )}
                                </div>


                                <div className="project-title">
                                    מנחה: {project.instructor_first_name && project.instructor_last_name
                                    ? `${project.instructor_first_name} ${project.instructor_last_name}`
                                    : "בטיפול"}
                                </div>
                                <div className="project-description">{project.description}</div>

                                <div className="project-github">
                                    <div>קישור לגיטהאב</div>
                                    {project.link_to_github ? (
                                        <a href={project.link_to_github} target="_blank" rel="noopener noreferrer">
                                            {project.link_to_github}
                                        </a>
                                    ) : (
                                        <span>לא הוזן קישור לגיטהאב</span>
                                    )}
                                </div>
                                <div className="project-tech-wrapper">
                                    {project.technologies?.length > 0 && (
                                        <div className="project-technologies">
                                            <h4>טכנולוגיות בפרויקט:</h4>
                                            <ul>
                                                {project.technologies.map((tech) => (
                                                    <li key={tech.id}>
                                                        {tech.language}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="notes-container">
                                {commentsLoading ? (
                                    <div>טוען סיכום הערות...</div>
                                ) : commentsError ? (
                                    <div className="error">{commentsError}</div>
                                ) : commentsSummary ? (
                                    <>
                                        <h4>סה"כ הערות: </h4>
                                        <div>בוצעו והושלמו: {commentsSummary.doneAndCompleted?.length || 0}</div>
                                        <div>בוצעו וממתינים לאישור: {commentsSummary.doneButNotCompleted?.length || 0}</div>
                                        <div>ממתינים לטיפול: {commentsSummary.notDone?.length || 0}</div>

                                        <div className="button-container" style={{ marginTop: "10px", justifyContent: "space-between" }}>
                                            <button onClick={() => setShowComments(true)}>תצוגה מפורטת</button>
                                            {isProjectInstructor && (
                                                <button className="add-note-button" onClick={() => setShowAddNote(true)}>➕ הוסף הערה</button>
                                            )}
                                        </div>
                                    </>
                                ) : (
                                    <div>אין הערות לפרויקט זה</div>
                                )}

                                {!commentsLoading && !commentsError && commentsSummary?.totalCount === 0 && (
                                    <div className="button-container" style={{ marginTop: "10px" }}>
                                        {isProjectInstructor && (
                                            <button className="add-note-button" onClick={() => setShowAddNote(true)}>
                                                ➕ הוסף הערה
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="pdfView">
                            {pdfUrl ? (
                                <iframe src={pdfUrl} title="Project PDF" className="pdf-iframe" />
                            ) : (
                                <div className="no-pdf-message">לפרויקט זה לא נוסף מסמך</div>
                            )}
                        </div>
                    </div>


                </div>
            </div>


            {showComments && (
                <Modal onClose={() => setShowComments(false)} width="70vw">
                    <CommentsProject comments={commentsSummary} nav="instructor"  isProjectInstructor={isProjectInstructor} />
                </Modal>
            )}

            {showAddNote && (
                <Modal onClose={() => setShowAddNote(false)} width="40vw">
                    <AddNote
                        projectTitle={project.title}
                        onClose={() => setShowAddNote(false)}
                        onSave={handleSaveNote}
                    />
                </Modal>
            )}
            {showUpdateStage && (
                <Modal onClose={() => setShowUpdateStage(false)} width="40vw">
                    <UpdateProjectStage
                        projectId={projectId}
                        onClose={() => setShowUpdateStage(false)}
                        onUpdate={() => setGradesRefresh(prev => !prev)}
                    />
                </Modal>
            )}
            {showUpdateInstructor && (
                <Modal onClose={() => setShowUpdateInstructor(false)} width="40vw">
                    <UpdateInstructor
                        projectId={projectId}
                        instructorId={project.instructor_id}
                        onClose={() => setShowUpdateInstructor(false)}
                        onUpdated={refreshProject}
                    />
                </Modal>
            )}

            {showAddStudents && (
                <Modal onClose={() => setShowAddStudents(false)} width="40vw">
                    <AddStudentToProject
                        onClose={() => setShowAddStudents(false)}
                        onUpdated={refreshProject}
                        projectId={projectId}
                        studentId={project.student_id1}
                    />
                </Modal>
            )}
        </div>
    );
};

export default ProjectDetails;